import {Component, Input, ElementRef, OnInit, OnChanges} from '@angular/core';
import * as d3 from 'd3';
import { RevenueMarginChart } from './revenue-margin-chart.interface';
import { HomeService } from 'src/app/services/home/home.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-revenue-margin-chart',
  templateUrl: './revenue-margin-chart.component.html',
  styleUrls: ['./revenue-margin-chart.component.scss'],
})
export class RevenueMarginChartComponent implements OnInit, OnChanges {
  data: RevenueMarginChart[] = [];
  loading: boolean = false;
  chart: any;
  x: any;
  y: any;
  maxValue: number = 0;
  svgWidth: number = 750;
  svgHeight: number = 340;
  barWidth: number = 25;
  gap = 12;
  // Set up chart dimensions
  margin = { top: 40, right: 30, bottom: 80, left: 50 };
  width = this.svgWidth - this.margin.left - this.margin.right;
  height = this.svgHeight - this.margin.top - this.margin.bottom;
  legends = {
    revenue: {
      color: '#71b06b',
      text: 'Revenue ($)',
      key: 'revenueGenerated',
    },
    cost: {
      color: '#477dc1',
      text: 'Incurred ($)',
      key: 'incurredCost',
    },
    margin: {
      color: '#f3a714',
      text: 'Margin',
      key: 'marginPercentage',
    },
    projection: {
      color: '#fff',
      text: 'Projection ($)',
      key: 'isProjected',
    },
  };
  @Input() chartData: any;

  constructor(
    private elementRef: ElementRef,
    private homeServiceRef: HomeService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    let _this = this;
    this.loading = true;
    const id = this.route.snapshot.paramMap.get('id');
    /* if (id)
     this.homeServiceRef.getRevenueMargin(id).subscribe((res) => {
       if (!!res) {
         this.data = res;
         this.loading = false;
         this.getChart(this.data);
       }
     }); */
  }

  ngOnChanges(changes: any) {
    if (changes.chartData.currentValue) {
      d3.select(this.elementRef.nativeElement)
        .select('#revenueMarginChart').select('*').remove();
      this.data = changes.chartData.currentValue;
      this.getChart(this.data)
    }
  }

  getChart(data: RevenueMarginChart[]): void {
    // Access the native element
    const svg = d3
      .select(this.elementRef.nativeElement)
      .select('#revenueMarginChart')
      .attr('viewBox', `0,0, ${this.svgWidth},${this.svgHeight}`);

    // Create the SVG element
    this.chart = svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // Create scales
    this.x = d3
      .scaleBand()
      .domain(data.map((d) => d.period))
      .range([0, this.width])
      .padding(0.5);

    this.maxValue =
      d3.max(data, (d) => Math.max(d.revenueGenerated, d.marginPercentage)) ||
      1000;
    this.y = d3
      .scaleLinear()
      .domain([0, this.maxValue])
      .range([this.height, 0]);

    // Create axis
    const xAxis = d3.axisBottom(this.x);

    // Add axes to the chart
    this.chart
      .append('g')
      .attr('class', 'x-axis')
      .attr('color', 'rgba(255, 255, 255, 0.2)')
      .attr('transform', `translate(0,${this.height})`)
      .call(xAxis);

    // Add gridlines for y-axis
    this.chart
      .append('g')
      .attr('class', 'gridlines')
      .attr('color', 'rgba(255, 255, 255, 0.1)')
      .call(d3.axisLeft(this.y).tickSize(-this.width))
      .selectAll('.gridlines g')
      .append('text')
      .text((d: any, i: number) => i * 10 + '%')
      .attr('x', this.width + this.margin.right - 5)
      .attr('dy', 4);

    // Add Legends
    this.addLegends();

    // Add Margin bar chart
    this.drawBarChart();
    // Add projected line
    this.drawProjectedLine();

    // Add Revenue line path
    this.drawDottedLineChart('revenueGenerated', this.legends.revenue.color);
    // Add Cost line path
    this.drawDottedLineChart('incurredCost', this.legends.cost.color);
  }

  // Draw dotted line chart
  drawDottedLineChart(
    key: 'revenueGenerated' | 'incurredCost',
    color: string = '#FFF'
  ) {
    // Add line dotted line path
    this.drawLineChart(
      key,
      this.data.filter((d) => !d.isProjected),
      color,
      true
    );
    // Add line solid path
    this.drawLineChart(key, this.data, color);
    // Add dot chart
    this.drawDotChart(key, color);
    // Add label
    this.drawLabel(key);
  }

  drawLineChart(
    key: 'revenueGenerated' | 'incurredCost',
    data: RevenueMarginChart[],
    lineColor: string = '#FFF',
    isDotted: boolean = false
  ) {
    // Create line generator
    const generateLine = d3
      .line<RevenueMarginChart>()
      .x((d) => this.x(d.period) || 0)
      .y((d) => this.y(d[key]) || 0);

    // Add line path
    this.chart
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('stroke', lineColor)
      .attr('stroke-linejoin', 'round')
      .attr('stroke-linecap', 'round')
      .attr('stroke-dasharray', isDotted ? '0' : '3,3')
      .attr('stroke-width', isDotted ? 1.5 : 1)
      .attr('d', generateLine);
  }

  // Add dot chart
  drawDotChart(
    key: 'revenueGenerated' | 'incurredCost',
    fill: string = '#FFF'
  ) {
    this.chart
      .selectAll('.circle')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('class', 'dot')
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('cx', (d: any) => this.x(d.period) || 0)
      .attr('cy', (d: any) => this.y(d[key]) || 0)
      .attr('r', 3)
      .style('fill', fill);
  }

  // Draw label for the line chart
  drawLabel(
    key: 'revenueGenerated' | 'incurredCost',
    color: string = '#cbcbd2'
  ) {
    this.chart
      .selectAll('.label')
      .data(this.data)
      .enter()
      .append('text')
      .attr('x', (d: any) => this.x(d.period) || 0)
      .attr('y', (d: any) => this.y(d[key]) || 0)
      .attr('transform', `translate(${this.gap}, 0)`)
      .attr('dx', (d: any) => d[key].toString().length * -3)
      .attr('dy', '-6')
      .attr('font-size', '12px')
      .text((d: any) => d[key])
      .style('fill', color);
  }

  // Draw bar chart
  drawBarChart() {
    const calcY = (percentage: number) =>
      this.height - this.height * (percentage / 100);
    const calcHeight = (percentage: number) => this.height * (percentage / 100);
    const barWidth = this.barWidth;
    const barColor = this.legends.margin.color;
    // Define the gradient
    const gradient = this.chart
      .append('defs')
      .append('linearGradient')
      .attr('id', 'bar-gradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');

    gradient
      .append('stop')
      .attr('offset', '70%')
      .attr('stop-color', this.legends.margin.color); // Start color

    gradient.append('stop').attr('offset', '71%').attr('stop-color', '#0000'); // End color

    // Define the pattern
    const pattern = this.chart
      .append('defs')
      .append('pattern')
      .attr('id', 'bar-pattern')
      .attr('width', '4')
      .attr('height', '4')
      .attr('patternUnits', 'userSpaceOnUse')
      .attr('patternTransform', 'rotate(90)');

    pattern
      .append('rect')
      .attr('width', '4')
      .attr('height', '4')
      .attr('fill', 'url(#bar-gradient)');

    // Append bars to the chart
    this.chart
      .selectAll('.barChart')
      .data(this.data)
      .enter()
      .append('g')
      .attr('class', 'bar')
      .attr(
        'transform',
        (d: any) =>
          `translate(${this.x(d.period)}, ${calcY(d.marginPercentage)})`
      )
      .each(function (this: SVGGElement, d: any) {
        // Append rect to the current g element
        d3.select(this)
          .append('rect')
          .attr('class', 'barChart')
          .attr('width', barWidth)
          .attr('height', (d: any) => calcHeight(d.marginPercentage) || 0)
          .style('fill', (d: any) =>
            !d.isProjected ? barColor : 'url(#bar-pattern)'
          );

        // Append title to the current g element
        d3.select(this)
          .append('text')
          .text(`${d.marginPercentage}%`)
          .attr('x', 0)
          .attr('y', -4)
          .attr('font-size', '12px')
          .attr('text-anchor', 'center')
          .style('fill', '#cbcbd2');
      });
  }

  // Add Legends
  addLegends() {
    const legend = this.chart
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(0, ${this.height + this.margin.top} )`);

    // Revenue legend
    const generateLegends = (color: string, text: string, x: number) => {
      const gLegend = legend
        .append('g')
        .attr('transform', `translate(${x}, 0)`);
      gLegend.append('circle').attr('r', 5).attr('fill', color).attr('cy', -4);
      gLegend.append('text').text(text).attr('x', 10);
    };

    Object.values(this.legends).forEach((l, i) => {
      generateLegends(l.color, l.text, i * 95);
    });
  }

  // Projected line
  drawProjectedLine() {
    const data = this.data.filter((d) => !d.isProjected);
    const projection = this.chart
      .append('g')
      .attr('class', 'projection-divider')
      .attr(
        'transform',
        `translate(${this.x(data[data.length - 1].period) + this.gap}, 0)`
      );

    projection
      .append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', this.height + this.margin.bottom + this.margin.top)
      .attr('stroke', '#FFF')
      .attr('stroke-width', 1);

    projection
      .append('circle')
      .attr('r', 5)
      .attr('fill', '#FFF')
      .attr('cy', 0)
      .attr('cx', 0);

    projection
      .append('circle')
      .attr('r', 5)
      .attr('fill', '#FFF')
      .attr('cy', this.height + this.margin.bottom + this.margin.top - 45)
      .attr('cx', 0);
  }
}
